import { useQueryClient } from '@tanstack/react-query';
import type { MediaDto } from '@str/api/types';
import { useRef, useState, type DragEvent } from 'react';
import { useToast } from '@/components/toast';
import { ApiError, errorMessage, uploadFile } from '@/lib/api';
import { MediaPreview } from '@/components/MediaPreview';
import { linkProblem, NOT_A_LINK } from '@/lib/media-link';
import { checkPhoto, SMALL_PHOTO_NOTE } from '@/lib/images';
import { useActiveProperty } from '@/lib/property-store';
import { mediaApi, useMedia } from '@/lib/queries';
import { mediaStyle } from '@/lib/scenes';

type Tab = 'All' | 'Listing' | 'Uploaded' | 'Generated';

function fmtDuration(s: number) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function tagStyle(m: MediaDto) {
  if (m.source === 'generated')
    return { bg: 'var(--accent-tint)', fg: 'var(--accent)', label: '✦ Generated' };
  if (m.source === 'reference') return { bg: '#111', fg: '#fff', label: 'Reference' };
  return {
    bg: 'rgba(255,255,255,.9)',
    fg: '#555',
    label: m.source === 'uploaded' ? 'Uploaded' : 'Listing',
  };
}

const EMPTY: Record<Tab, string> = {
  All: 'Nothing here yet. Import a listing or upload a photo, video or music.',
  Listing: 'Nothing here yet from a listing. Import one and its photos land here.',
  Uploaded: 'Nothing uploaded yet. Drop files anywhere on this page or paste a link.',
  Generated: 'Nothing generated yet. Every picture and reel made here is kept here.',
};

export function Media() {
  const { active } = useActiveProperty();
  const media = useMedia(active.id);
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('All');
  const [linkOpen, setLinkOpen] = useState(false);
  const [link, setLink] = useState('');
  // What is wrong with the link, under the field; stays until the host changes the link.
  const [linkError, setLinkError] = useState<string | null>(null);
  const [preview, setPreview] = useState<MediaDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['media', active.id] });
  const upload = async (files: FileList | File[]) => {
    setBusy(true);
    try {
      let sent = 0;
      let small = false;
      for (const f of Array.from(files)) {
        const check = await checkPhoto(f);
        if (!check.ok) {
          toast(check.reason, { kind: 'error' });
          continue;
        }
        small ||= check.small;
        await uploadFile(active.id, f);
        sent += 1;
      }
      await refresh();
      if (sent) toast(small ? SMALL_PHOTO_NOTE : `Uploaded ${sent} file${sent > 1 ? 's' : ''}`);
    } catch (e) {
      toast(errorMessage(e), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const addLink = async () => {
    const url = link.trim();
    if (!url) return;
    const why = linkProblem(url);
    if (why) {
      setLinkError(why);
      return;
    }
    setBusy(true);
    try {
      await mediaApi.addLink(active.id, url);
      setLink('');
      setLinkError(null);
      setLinkOpen(false);
      await refresh();
      toast('Reference added');
    } catch (e) {
      // The API's own no is about the link too (a schema message like "url: Invalid URL"):
      // it belongs under the field, in plain words, not in a passing toast.
      if (e instanceof ApiError && e.code === 'validation') setLinkError(NOT_A_LINK);
      else toast(errorMessage(e), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const remove = async (m: MediaDto) => {
    try {
      await mediaApi.remove(m.id);
      await refresh();
    } catch (e) {
      toast(errorMessage(e), { kind: 'error' });
    }
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) void upload(e.dataTransfer.files);
  };

  // What the host sees instead of an empty grid: loading, offline, failed, or truly nothing.
  const status: {
    kind: 'loading' | 'offline' | 'failed' | 'empty';
    text?: string;
    retry?: boolean;
  } | null =
    media.isPending && media.fetchStatus === 'paused'
      ? {
          kind: 'offline',
          text: "You're offline. Your media is still saved; it shows once you're back online.",
          retry: true,
        }
      : media.isPending
        ? { kind: 'loading' }
        : media.isError
          ? {
              kind: 'failed',
              text: "Couldn't load your media. " + errorMessage(media.error),
              retry: true,
            }
          : null;
  const shown = (media.data ?? []).filter(
    (m) =>
      tab === 'All' ||
      m.source === tab.toLowerCase() ||
      (tab === 'Uploaded' && m.source === 'reference'),
  );

  return (
    <div
      className="a-rise media-page"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'auto',
        outline: dragging ? '2px dashed rgba(0,0,0,.3)' : 'none',
        outlineOffset: -8,
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <div className="media-head">
        <div className="media-head-main">
          <div className="media-head-title">Media</div>
          <div className="pilltabs" role="tablist">
            {(['All', 'Listing', 'Uploaded', 'Generated'] as Tab[]).map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={tab === t}
                className={`pilltab hv${tab === t ? ' on' : ''}`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <span className="media-head-hint">
          {busy ? 'Uploading…' : 'Drop files anywhere on this page'}
        </span>
      </div>
      {linkOpen && (
        <div
          className="card float a-pop media-link-form"
          style={{ padding: 8, display: 'flex', gap: 8, marginBottom: 12, borderRadius: 14 }}
        >
          <input
            className="input"
            style={{ border: 0 }}
            placeholder="https://… image or video link"
            value={link}
            onChange={(e) => {
              setLink(e.target.value);
              setLinkError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && void addLink()}
            autoFocus
            aria-label="Media link"
            aria-invalid={linkError ? true : undefined}
            aria-describedby={linkError ? 'media-link-error' : undefined}
          />
          <button
            className="btn btn-primary hv"
            style={{ borderRadius: 10 }}
            onClick={() => void addLink()}
            disabled={busy}
          >
            Add
          </button>
          <button
            className="btn btn-ghost hv"
            style={{ borderRadius: 10 }}
            onClick={() => {
              setLinkOpen(false);
              setLinkError(null);
            }}
          >
            Cancel
          </button>
          {linkError && (
            <div id="media-link-error" className="err media-link-error" role="alert">
              {linkError}
            </div>
          )}
        </div>
      )}
      {status && (
        <div
          className="media-status"
          role={status.kind === 'loading' ? 'status' : undefined}
          aria-label={status.kind === 'loading' ? 'Loading your media' : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            marginBottom: 12,
            borderRadius: 12,
            background: status.kind === 'loading' ? 'transparent' : 'var(--bg)',
            fontSize: 13.5,
            color: '#555',
          }}
        >
          {status.kind === 'loading' ? (
            <>
              {[0, 1, 2].map((i) => (
                <span key={i} className="sk" style={{ width: 120, height: 90, borderRadius: 12 }} />
              ))}
            </>
          ) : (
            <>
              <span>{status.text}</span>
              {status.retry && (
                <button
                  type="button"
                  className="btn-pill hv"
                  onClick={() => void media.refetch()}
                  style={{ marginLeft: 'auto' }}
                >
                  Try again
                </button>
              )}
            </>
          )}
        </div>
      )}
      <div className="media-grid">
        {!status && shown.length === 0 && (
          <div
            className="media-empty"
            style={{
              gridColumn: '1 / -1',
              padding: '18px 4px',
              fontSize: 13.5,
              color: '#777',
            }}
          >
            {EMPTY[tab]}
          </div>
        )}
        {shown.map((m, i) => {
          const t = tagStyle(m);
          return (
            <div
              key={m.id}
              className="hv tile"
              style={{
                borderRadius: 14,
                // An audio file has nothing to paint; it gets the scene and a note.
                ...(m.kind === 'audio' ? mediaStyle({ scene: m.scene }) : mediaStyle(m)),
                position: 'relative',
                overflow: 'hidden',
                minHeight: 0,
                animation: 'rise .5s both',
                animationDelay: `${(0.05 + i * 0.035).toFixed(3)}s`,
              }}
              title={m.label}
            >
              {m.kind === 'video' && m.url && (
                <video
                  src={m.url}
                  crossOrigin="anonymous"
                  muted
                  playsInline
                  preload="metadata"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}
              <span
                style={{
                  position: 'absolute',
                  left: 8,
                  top: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '3px 7px',
                  borderRadius: 99,
                  background: t.bg,
                  color: t.fg,
                  pointerEvents: 'none',
                }}
              >
                {t.label}
              </span>
              {m.contentMediaError && (
                <span
                  title={m.contentMediaError}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 7px',
                    borderRadius: 99,
                    background: '#a33a2a',
                    color: '#fff',
                    pointerEvents: 'none',
                    maxWidth: 'calc(100% - 16px)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  Can&apos;t be used: {m.contentMediaError}
                </span>
              )}
              {m.kind === 'audio' && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 34,
                    color: 'rgba(255,255,255,.9)',
                    textShadow: '0 1px 8px rgba(0,0,0,.4)',
                    pointerEvents: 'none',
                  }}
                >
                  ♪
                </span>
              )}
              {m.durationSeconds != null && (
                <span
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 7px',
                    borderRadius: 99,
                    background: 'rgba(0,0,0,.5)',
                    color: '#fff',
                    pointerEvents: 'none',
                  }}
                >
                  {m.kind === 'audio' ? '♪' : '▶'} {fmtDuration(m.durationSeconds)}
                </span>
              )}
              <button
                type="button"
                className="tile-open"
                aria-label={`Inspect ${m.label}`}
                onClick={() => setPreview(m)}
              />
              <span
                className="kbd"
                style={{
                  position: 'absolute',
                  left: 8,
                  bottom: 8,
                  color: 'rgba(255,255,255,.85)',
                  textShadow: '0 1px 6px rgba(0,0,0,.5)',
                }}
              >
                {m.label}
              </span>
              {m.source !== 'generated' && (
                <button
                  type="button"
                  className="tile-x hv"
                  aria-label={`Remove ${m.label}`}
                  onClick={() => void remove(m)}
                  style={{
                    position: 'absolute',
                    right: 8,
                    bottom: 8,
                    width: 22,
                    height: 22,
                    borderRadius: 99,
                    border: 0,
                    background: 'rgba(0,0,0,.55)',
                    color: '#fff',
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="dashed-tile hv"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
        >
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 99,
              background: '#fff',
              border: '1px solid rgba(0,0,0,.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              color: '#111',
            }}
          >
            ↑
          </span>
          Upload
          <span
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setLinkOpen(true);
            }}
            onKeyDown={(e) => e.key === 'Enter' && setLinkOpen(true)}
            style={{ textDecoration: 'underline', fontSize: 11.5 }}
          >
            or paste a link
          </span>
        </button>
        <MediaPreview
          item={preview}
          onClose={() => setPreview(null)}
          onRename={async (label) => {
            if (!preview) return;
            const { media: renamed } = await mediaApi.rename(preview.id, label);
            setPreview(renamed);
            toast('Renamed');
            await refresh();
          }}
        />
        <input
          ref={fileInput}
          type="file"
          accept="image/*,video/mp4,video/quicktime,video/webm"
          multiple
          hidden
          onChange={(e) => e.target.files && void upload(e.target.files)}
        />
      </div>
    </div>
  );
}
